import { alpha, createTheme, emphasize } from '@mui/material/styles';

export const msTeamsTheme = (prefersDarkMode?: null | boolean) => {
  function modeColor<T>(darkColor: T, lightColor: T) {
    return prefersDarkMode ? darkColor : lightColor;
  }

  const teamsColors = {
    primary: '#6264A7',
    secondary: '#464775',
    success: '#92C353',
    error: '#D74654',
    warning: '#F3C200',
    info: '#31A4E2',
    neutral: {
      dark: '#201F1F',
      medium: '#252423',
      light: '#F3F2F1',
      background: '#181818',
      paper: '#2B2B2B',
      sidebar: '#1B1A1B',
    },
  };

  return createTheme({
    typography: {
      fontFamily: ['Segoe UI', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(', '),
      h1: { fontWeight: 300, fontSize: '96px', color: modeColor('#ffffff', '#242424') },
      h2: { fontWeight: 300, fontSize: '59px', color: modeColor('#ffffff', '#242424') },
      h3: { fontWeight: 400, fontSize: '47px', color: modeColor('#ffffff', '#242424') },
      h4: { fontWeight: 800, fontSize: '33px' },
      h5: { fontWeight: 600, fontSize: '24px' },
      h6: { fontWeight: 500, fontSize: '20px' },
      subtitle1: { fontWeight: 500, fontSize: '16px' },
      subtitle2: { fontWeight: 500, fontSize: '14px' },
      body1: { fontWeight: 400, fontSize: '16px' },
      body2: { fontWeight: 400, fontSize: '14px' },
      button: { fontWeight: 700, fontSize: '14px', textTransform: 'none' },
      caption: { fontWeight: 600, fontSize: '12px' },
      overline: { fontWeight: 400, fontSize: '10px' },
    },
    palette: {
      mode: prefersDarkMode ? 'dark' : 'light',
      primary: { main: teamsColors.primary, contrastText: '#ffffff' },
      secondary: { main: teamsColors.secondary, contrastText: '#ffffff' },
      success: { main: teamsColors.success, contrastText: '#ffffff' },
      error: { main: teamsColors.error, contrastText: '#ffffff' },
      warning: { main: teamsColors.warning, contrastText: '#ffffff' },
      info: { main: teamsColors.info, contrastText: '#ffffff' },
      background: {
        default: modeColor(teamsColors.neutral.background, teamsColors.neutral.light),
        paper: modeColor(teamsColors.neutral.paper, '#ffffff'),
        drawer: modeColor(teamsColors.neutral.sidebar, '#ffffff'),
        filterActive: modeColor(teamsColors.neutral.medium, teamsColors.neutral.light),
      },
      text: {
        primary: modeColor('#ffffff', '#242424'),
        secondary: modeColor('#A6A7DC', '#6264A7'),
        disabled: modeColor('#5D5D5D', '#C1C1C1'),
        button: modeColor('#ffffff', '#000000'),
      },
      divider: modeColor('#373737', '#E1E1E1'),
      action: {
        active: modeColor('#ffffff', '#000000'),
        hover: modeColor(alpha(teamsColors.primary, 0.08), alpha(teamsColors.primary, 0.08)),
      },
      border: {
        default: modeColor('#373737', '#E1E1E1'),
        hover: modeColor(teamsColors.primary, teamsColors.primary),
        active: modeColor(teamsColors.primary, teamsColors.primary),
        dark: modeColor('#201F1F', '#ffffff'),
        button: modeColor('#373737', '#E1E1E1'),
        banner: modeColor('#373737', '#E1E1E1'),
      },
      graph: {
        primary: modeColor(teamsColors.primary, teamsColors.primary),
        success: modeColor(teamsColors.success, teamsColors.success),
        warning: modeColor(teamsColors.warning, teamsColors.warning),
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.unstable_sx({
              fontWeight: 700,
              borderRadius: 1,
              textTransform: 'none',
              boxShadow: 'none',
              lineHeight: 1.6,
              whiteSpace: 'nowrap',

              ':hover': { boxShadow: 'none' },
            }),
          sizeMedium: ({ theme }) => ({
            padding: theme.spacing(0.5, 2),
          }),
          sizeSmall: ({ theme }) => ({
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(0.5),
            minHeight: theme.spacing(3.6),
          }),
        },
        variants: [
          {
            props: { variant: 'outlined' },
            style: ({ theme }) => ({
              padding: theme.spacing(0.5, 2),
              border: `1px solid ${teamsColors.primary}`,
              backgroundColor: alpha(teamsColors.primary, 0.08),
              color: teamsColors.primary,

              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
                borderColor: theme.palette.border.default,
                backgroundColor: alpha(theme.palette.background.default, 0.08),
              },

              '&:hover:not(.Mui-disabled)': {
                backgroundColor: alpha(teamsColors.primary, 0.16),
                borderColor: teamsColors.primary,
              },
            }),
          },
          {
            props: { variant: 'text' },
            style: ({ theme }) => ({
              color: teamsColors.primary,
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
              },
            }),
          },
          {
            props: { variant: 'contained' },
            style: ({ theme }) => ({
              padding: theme.spacing(0.5, 2),

              '&.MuiButton-containedPrimary': {
                '&:hover': {
                  backgroundColor: teamsColors.primary,
                },
              },
            }),
          },
          {
            props: { variant: 'outlined-filters' },
            style: ({ theme }) => ({
              border: `1px solid ${theme.palette.border.default}`,
              padding: theme.spacing(0.5, 1, 0.5, 2),
              minWidth: 180,
              display: 'flex',
              justifyContent: 'flex-start',

              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
              },

              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
                borderColor: theme.palette.border.default,
                backgroundColor: modeColor(theme.palette.background.default, theme.palette.background.paper),
              },

              '&:hover:not(.Mui-disabled)': {
                backgroundColor: modeColor(alpha(teamsColors.primary, 0.08), alpha(teamsColors.primary, 0.08)),
                borderColor: modeColor(teamsColors.primary, teamsColors.primary),
              },
            }),
          },
          {
            props: { variant: 'contained-filters' },
            style: ({ theme }) => ({
              border: `1px solid ${teamsColors.primary}`,
              padding: theme.spacing(0.5, 1, 0.5, 2),
              lineHeight: 1.6,
              color: teamsColors.primary,
              minWidth: 180,
              display: 'flex',
              justifyContent: 'flex-start',
              backgroundColor: alpha(teamsColors.primary, 0.08),

              '& .MuiButton-endIcon': {
                marginLeft: 'auto',
              },

              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
                borderColor: theme.palette.border.default,
                backgroundColor: modeColor(theme.palette.background.default, theme.palette.background.paper),
              },

              '&:hover:not(.Mui-disabled)': {
                backgroundColor: alpha(teamsColors.primary, 0.16),
                borderColor: teamsColors.primary,
              },
            }),
          },
          {
            props: { variant: 'tag-action-button' },
            style: ({ theme }) => ({
              variant: 'contained',
              width: theme.spacing(6),
              height: theme.spacing(2.5),
              backgroundColor: alpha(theme.palette.primary.main, 0.14),
              borderRadius: '18px',
              '& .text': { color: theme.palette.button.lightText },
            }),
          },
          {
            props: { variant: 'outlined-narrow' },
            style: ({ theme }) => ({
              border: `1px solid ${teamsColors.primary}`,
              color: teamsColors.primary,
              minWidth: 0,
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),

              '&.MuiButton-outlined-narrowSizeSmall': {
                fontSize: theme.typography.pxToRem(10),
                padding: theme.spacing(0.5),

                '& .MuiIcon-root': {
                  fontSize: theme.typography.pxToRem(21),
                },
              },
            }),
          },
          {
            props: { variant: 'outlined-revoke' },
            style: ({ theme }) => ({
              color: theme.palette.text.disabled,
              border: `1px solid ${theme.palette.text.disabled}`,
            }),
          },
          {
            props: { variant: 'outlined-base' },
            style: ({ theme }) => ({
              border: `1px solid ${theme.palette.border.default}`,
              color: theme.palette.text.primary,
              minWidth: 0,
              paddingLeft: theme.spacing(3),
              paddingRight: theme.spacing(3),
            }),
          },
          {
            props: { variant: 'contained-icon-only' },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              minWidth: 0,
              paddingLeft: 0,
              paddingRight: 0,

              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },

              '& .MuiButton-endIcon': {
                marginLeft: 0,
                marginRight: 0,
              },

              '& .MuiButton-startIcon': {
                marginLeft: 0,
                marginRight: 0,
              },

              '&.MuiButton-fullWidth': {
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              },
            }),
          },
        ],
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          labelPlacementStart: {
            marginRight: -6,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: modeColor(teamsColors.primary, teamsColors.primary),
          },
        },
        defaultProps: {
          underline: 'none',
        },
      },
      MuiInput: {
        styleOverrides: {
          input: ({ theme }) =>
            theme.unstable_sx({
              fontWeight: 400,
            }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.unstable_sx({
              borderRadius: '4px',
              borderColor: theme.palette.border.default,
            }),
        },
        variants: [
          {
            props: { variant: 'standard' },
            style: ({ theme }) => ({
              '& .Mui-focused': {
                backgroundColor: theme.palette.background.paper,
                '&:before': {
                  borderColor: `${theme.palette.primary.main} !important`,
                },

                '& .MuiInputBase-input': {
                  '&:hover': {
                    '&::placeholder': {
                      color: theme.palette.text.disabled,
                    },
                  },
                },
              },

              '& .MuiInputBase-root': {
                borderColor: theme.palette.text.dropdown,
                '&:before': {
                  borderColor: theme.palette.text.dropdown,
                },

                '&:after': {
                  display: 'none',
                },

                '&:hover': {
                  '&:before': {
                    borderWidth: '1px !important',
                  },
                },
              },

              '& .MuiInputBase-input': {
                padding: theme.spacing(0.8, 0.5),

                '&:hover': {
                  '&::placeholder': {
                    color: theme.palette.primary.main,
                    opacity: 1,
                  },
                },

                '&::placeholder': {
                  color: theme.palette.text.disabled,
                  opacity: 1,
                },
              },

              '& .MuiInputBase-adornedEnd': {
                paddingRight: theme.spacing(0.5),
              },
            }),
          },
          {
            props: { variant: 'outlined' },
            style: ({ theme }) => ({
              borderColor: theme.palette.border.default,
            }),
          },
        ],
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            padding: 0,
          },
          notchedOutline: ({ theme }) => ({
            borderColor: theme.palette.border.default,
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          inputSizeSmall: ({ theme }) => ({
            lineHeight: '1.2rem',
            fontSize: '14px',
            padding: `${theme.spacing(0.75, 1.25)} !important`,

            '&.MuiAutocomplete-input': {
              padding: `0 !important`,
            },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxShadow: `0 0 0 1px ${theme.palette.border.default}`,
          }),
        },
        variants: [
          {
            props: { variant: 'details' },
            style: ({ theme }) => ({
              boxShadow: `none`,
              padding: theme.spacing(2),
              borderRadius: theme.shape.borderRadius,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }),
          },
        ],
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.unstable_sx({
              boxShadow: 'none',
            }),
        },
        variants: [
          {
            props: { variant: 'filled-banner' },
            style: ({ theme }) => ({
              border: `1px solid ${theme.palette.border.default}`,
              borderRadius: theme.spacing(1),
              padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              alignItems: 'center',
              minHeight: '70px',
              paddingRight: theme.spacing(3.75),
              paddingLeft: theme.spacing(2.5),
            }),
          },
        ],
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            boxShadow: theme.shadows[15],
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2.5, 2),
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(0, 2, 2, 2),
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: teamsColors.neutral.medium,
          },
          arrow: { color: teamsColors.neutral.medium },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 'auto',
            borderBottom: `1px solid ${theme.palette.border.default}`,
          }),
          indicator: {
            height: '1px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            minHeight: 'auto',
            minWidth: 'auto',
            padding: theme.spacing(1, 2),
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.border.default,
            padding: theme.spacing(1.5, 2),
          }),
          head: {
            textTransform: 'uppercase',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.MuiTableRow-hover': {
              '&:hover': {
                'td:first-of-type': {
                  position: 'relative',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '1px',
                    backgroundColor: theme.palette.primary.main,
                  },
                },
              },
            },
            '&.MuiTableRow-clickable': {
              cursor: 'pointer',
            },
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: ({ theme }) => ({
            margin: theme.spacing(1, 0),
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(0.5, 0),
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontWeight: 600,
            fontSize: '12px',
            borderBottom: `1px solid ${theme.palette.border.hover}`,
            padding: theme.spacing(1, 1.5),
            alignItems: 'center',
          }),
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: ({ theme }) => ({
            paddingLeft: `${theme.spacing(1.25)} !important`,
          }),
          paper: ({ theme }) => ({
            margin: theme.spacing(1, 0),

            '&:empty': {
              boxShadow: 'none',
            },
          }),
          listbox: ({ theme }) => ({
            padding: theme.spacing(0.5, 0),
          }),
          option: ({ theme }) => ({
            fontWeight: 600,
            fontSize: '12px',
            borderBottom: `1px solid ${theme.palette.border.hover}`,
            padding: theme.spacing(1, 1.5),
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.body2,
          }),
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {},
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.subtitle2,
            lineHeight: 1,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            ...theme.typography.subtitle2,
          }),
          avatar: ({ theme }) => ({
            '&.rt-chip-avatar': {
              marginLeft: theme.spacing(1),
              marginRight: 0,
            },
          }),
          label: ({ theme }) => ({
            '&.rt-chip-avatar': {
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),
            },
          }),
          deleteIcon: ({ theme }) => ({
            color: 'inherit',
            marginRight: theme.spacing(1),
          }),
          colorSuccess: ({ theme }) => ({
            color: theme.palette.success.main,
          }),
        },
        variants: [
          {
            props: { variant: 'filter' },
            style: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              backgroundColor: theme.palette.background.paper,

              '&:hover': {
                backgroundColor: emphasize(theme.palette.background.paper, 0.1),
              },
            }),
          },
          {
            props: { variant: 'filter-active' },
            style: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius,
              backgroundColor: theme.palette.background.filterActive,

              '&:hover': {
                backgroundColor: emphasize(theme.palette.background.filterActive, 0.1),
              },
            }),
          },
        ],
      },
      MuiAvatar: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.size-16': {
              width: theme.spacing(2),
              height: theme.spacing(2),
            },
            '&.size-20': {
              width: theme.spacing(2.5),
              height: theme.spacing(2.5),
            },
            '&.size-24': {
              width: theme.spacing(3),
              height: theme.spacing(3),
            },
          }),
        },
        variants: [
          {
            props: { variant: 'small' },
            style: {
              width: '24px',
              height: '24px',
              fontSize: '12px',
            },
          },
        ],
      },
    },
  });
};
